/*
  SoapTemplatesSettings — CRUD for SOAP note scaffolds.

  Each template holds:
    * name          — shown in the picker (e.g. "GLA:D reassessment")
    * description   — optional internal note
    * default_appointment_type — pre-fills the recording dialog
    * default_extra_context    — starter therapist context
    * instructions_for_llm     — appended to the LLM prompt so the
        generated note lands in the shape this therapist expects
    * default_exercises        — list pre-filled if the LLM didn't
        surface any exercises (skeleton with name/sets/reps/load/notes)
*/
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { StickyNote, Plus, Trash2, Save, Pencil, X, Loader2 } from "lucide-react";

function empty() {
  return {
    name: "",
    description: "",
    instructions_for_llm: "",
    default_appointment_type: "",
    default_extra_context: "",
    default_exercises: [],
    sort_order: 0,
  };
}

function ExRow({ row, onChange, onRemove }) {
  return (
    <div className="grid grid-cols-[1.6fr_70px_70px_70px_1fr_28px] gap-2 items-center">
      <Input value={row.name || ""} onChange={(e) => onChange({ ...row, name: e.target.value })} placeholder="Exercise" className="h-8 text-xs" />
      <Input value={row.sets || ""} onChange={(e) => onChange({ ...row, sets: e.target.value })} placeholder="Sets" className="h-8 text-xs" />
      <Input value={row.reps || ""} onChange={(e) => onChange({ ...row, reps: e.target.value })} placeholder="Reps" className="h-8 text-xs" />
      <Input value={row.load || ""} onChange={(e) => onChange({ ...row, load: e.target.value })} placeholder="Load" className="h-8 text-xs" />
      <Input value={row.notes || ""} onChange={(e) => onChange({ ...row, notes: e.target.value })} placeholder="Notes" className="h-8 text-xs" />
      <button type="button" onClick={onRemove} className="text-zinc-400 hover:text-red-600"><X className="h-4 w-4" /></button>
    </div>
  );
}

function EditorDialog({ open, template, onClose, onSaved }) {
  const [draft, setDraft] = useState(empty());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setDraft(template ? { ...empty(), ...template } : empty());
  }, [open, template]);

  async function save() {
    if (!draft.name?.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = {
        name: draft.name,
        description: draft.description || "",
        instructions_for_llm: draft.instructions_for_llm || "",
        default_appointment_type: draft.default_appointment_type || "",
        default_extra_context: draft.default_extra_context || "",
        default_exercises: draft.default_exercises || [],
        sort_order: Number(draft.sort_order) || 0,
      };
      const r = template?.id
        ? await api.patch(`/soap-notes/templates/${template.id}`, payload)
        : await api.post("/soap-notes/templates", payload);
      toast.success(template?.id ? "Template updated" : "Template created");
      onSaved?.(r.data);
      onClose?.();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="soap-template-editor">
        <DialogHeader>
          <DialogTitle>{template?.id ? "Edit template" : "New template"}</DialogTitle>
          <DialogDescription>
            Steer the AI toward the shape of note you always want for this session type.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-[1fr_120px] gap-3">
            <div>
              <Label className="text-xs">Name</Label>
              <Input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="e.g. GLA:D reassessment"
                className="h-9 text-sm"
                data-testid="soap-template-name"
              />
            </div>
            <div>
              <Label className="text-xs">Sort order</Label>
              <Input
                type="number"
                value={draft.sort_order}
                onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })}
                className="h-9 text-sm font-mono-num"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Description (internal — not sent to LLM)</Label>
            <Input
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              className="h-9 text-sm"
              placeholder="Optional — remind yourself when to use this"
            />
          </div>
          <div>
            <Label className="text-xs">Default appointment type</Label>
            <Input
              value={draft.default_appointment_type}
              onChange={(e) => setDraft({ ...draft, default_appointment_type: e.target.value })}
              className="h-9 text-sm"
              placeholder="e.g. Pilates 1:1"
            />
          </div>
          <div>
            <Label className="text-xs">Default therapist context</Label>
            <Textarea
              value={draft.default_extra_context}
              onChange={(e) => setDraft({ ...draft, default_extra_context: e.target.value })}
              className="min-h-[60px] text-sm"
              placeholder="Pre-fills the therapist context textarea when this template is picked."
              data-testid="soap-template-context"
            />
          </div>
          <div>
            <Label className="text-xs">LLM instructions (goes into the prompt)</Label>
            <Textarea
              value={draft.instructions_for_llm}
              onChange={(e) => setDraft({ ...draft, instructions_for_llm: e.target.value })}
              className="min-h-[100px] text-sm font-mono-num"
              placeholder={`e.g. "Always include the 3-min chair-stand result and pain-on-walking VAS in Objective. Use lay language for Plan so we can hand the note straight to the client."`}
              data-testid="soap-template-llm-instructions"
            />
            <div className="text-2xs text-zinc-500 mt-1">
              The AI will follow these instructions when generating this session's SOAP note.
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Default exercises (fallback if none in transcript)</Label>
              <Button
                size="sm" variant="ghost" type="button"
                onClick={() => setDraft({
                  ...draft,
                  default_exercises: [...(draft.default_exercises || []), { name: "", sets: "", reps: "", load: "", notes: "" }],
                })}
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Add row
              </Button>
            </div>
            <div className="space-y-1.5 mt-1">
              {(draft.default_exercises || []).map((row, i) => (
                <ExRow
                  key={i} row={row}
                  onChange={(v) => { const arr = [...draft.default_exercises]; arr[i] = v; setDraft({ ...draft, default_exercises: arr }); }}
                  onRemove={() => setDraft({ ...draft, default_exercises: draft.default_exercises.filter((_, j) => j !== i) })}
                />
              ))}
              {!(draft.default_exercises || []).length && (
                <div className="text-xs text-zinc-400 py-2">No default exercises.</div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="soap-template-save">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Save template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SoapTemplatesSettings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = new, {id, ...} = edit
  const [editorOpen, setEditorOpen] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/soap-notes/templates");
      setRows(r.data || []);
    } catch (e) {
      toast.error("Could not load templates", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function removeRow(t) {
    if (!confirm(`Delete template "${t.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/soap-notes/templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  return (
    <Card className="p-6 rounded-lg border border-zinc-200 shadow-none" data-testid="soap-templates-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            <StickyNote className="h-3.5 w-3.5" /> SOAP session templates
          </div>
          <div className="text-sm text-zinc-700 mt-1">
            Reusable scaffolds for common assessments — pick one before recording to
            shape the generated note exactly the way you like it.
          </div>
        </div>
        <Button
          onClick={() => { setEditing(null); setEditorOpen(true); }}
          data-testid="soap-template-new"
        >
          <Plus className="h-4 w-4 mr-1" /> New template
        </Button>
      </div>
      <div className="mt-4 overflow-x-auto">
        {loading ? (
          <div className="text-xs text-zinc-500 py-4">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-zinc-500 py-6 text-center border border-dashed border-zinc-200 rounded-md">
            No templates yet — create one to save time on your regular assessments.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="soap-templates-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Appointment type</th>
                <th className="py-2 pr-4">Exercises</th>
                <th className="py-2 pr-4">Instructions</th>
                <th className="py-2 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} className="border-b border-zinc-100" data-testid={`soap-template-row-${t.id}`}>
                  <td className="py-3 pr-4">
                    <div className="font-medium text-zinc-950">{t.name}</div>
                    {t.description && <div className="text-xs text-zinc-500">{t.description}</div>}
                  </td>
                  <td className="py-3 pr-4 text-zinc-700 text-xs">{t.default_appointment_type || "—"}</td>
                  <td className="py-3 pr-4 text-zinc-700 text-xs font-mono-num">{(t.default_exercises || []).length}</td>
                  <td className="py-3 pr-4 text-xs text-zinc-600 max-w-md">
                    <div className="line-clamp-2">{t.instructions_for_llm || "—"}</div>
                  </td>
                  <td className="py-3 pr-4">
                    <div className="flex gap-1.5">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => { setEditing(t); setEditorOpen(true); }}
                        data-testid={`soap-template-edit-${t.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        className="text-red-700 hover:text-red-800"
                        onClick={() => removeRow(t)}
                        data-testid={`soap-template-delete-${t.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <EditorDialog
        open={editorOpen}
        template={editing}
        onClose={() => setEditorOpen(false)}
        onSaved={() => load()}
      />
    </Card>
  );
}
